import type { User } from '@supabase/supabase-js';
import { resolveAuthEmail } from './authEmail';
import {
  fetchOwnPortalAccessRow,
  type PortalAccessRow,
} from './accessManagement';
import { allowlistRowToCrmUser } from './crmMembers';
import { emailToUsername, normalizeEmail } from './emailUtils';
import { formatDisplayPersonName } from './formatPersonName';
import type { AllowedEmail, CrmUser, UserRole } from '../types/crm';
import {
  isCrossPortalRole,
  isNeutralAuthPath,
  portalKindFromPath,
  readPortalChoice,
  type PortalKind,
} from './portal';
import {
  fetchOwnPortalDepartmentSlug,
  isElevatedDepartmentSlug,
  type ElevatedDepartmentSlug,
} from './portalUserProfiles';
export type PortalSideAccess = {
  hasAccess: boolean;
  profile: CrmUser | null;
};

export type PortalAccessMap = {
  email: string | null;
  /** From portal_user_profiles → application_form.departments.slug (any dept). */
  departmentSlug: string | null;
  /** From portal_user_profiles → departments.slug when IT/Management. */
  elevatedDepartment: ElevatedDepartmentSlug | null;
  crm: PortalSideAccess;
  it: PortalSideAccess;
  dem: PortalSideAccess;
  forms: PortalSideAccess;
  accounts: PortalSideAccess;
  report: PortalSideAccess;
  purchasing: PortalSideAccess;
  design: PortalSideAccess;
};

/** IT and management may switch between Sales CRM and IT Management workspaces. */
export { isCrossPortalRole } from './portal';

export function crossPortalUser(map: PortalAccessMap): boolean {
  if (map.elevatedDepartment) return true;
  const crmRole = map.crm.profile?.role;
  const itRole = map.it.profile?.role;
  return (
    (crmRole != null && isCrossPortalRole(crmRole)) ||
    (itRole != null && isCrossPortalRole(itRole))
  );
}

export function solePortalAccess(map: PortalAccessMap): PortalKind | null {
  const sides: PortalKind[] = [];
  if (map.crm.hasAccess) sides.push('crm');
  if (map.it.hasAccess) sides.push('it');
  if (map.dem.hasAccess) sides.push('dem');
  if (map.forms.hasAccess) sides.push('forms');
  if (map.report.hasAccess) sides.push('report');
  if (map.accounts.hasAccess) sides.push('accounts');
  if (map.purchasing.hasAccess) sides.push('purchasing');
  if (map.design.hasAccess) sides.push('design');
  return sides.length === 1 ? sides[0]! : null;
}

function sideFromWideRole(
  authUser: User,
  email: string,
  wide: PortalAccessRow,
  role: string | null | undefined,
): PortalSideAccess {
  const trimmed = String(role ?? '').trim();
  if (!trimmed) return { hasAccess: false, profile: null };
  const row: AllowedEmail = {
    id: wide.id,
    email: wide.email,
    display_name: wide.display_name,
    role: trimmed as UserRole,
    username: wide.username,
    auth_id: wide.auth_id,
    created_at: wide.created_at ?? '',
    department_id: wide.department_id,
  };
  return {
    hasAccess: true,
    profile: {
      ...allowlistRowToCrmUser(row),
      id: authUser.id,
      email: resolveAuthEmail(authUser) ?? normalizeEmail(email),
    },
  };
}

function mapFromWideRow(
  authUser: User,
  email: string,
  wide: PortalAccessRow,
  departmentSlug: string | null,
): PortalAccessMap {
  const elevatedDepartment: ElevatedDepartmentSlug | null = isElevatedDepartmentSlug(departmentSlug)
    ? departmentSlug
    : null;

  let crm = sideFromWideRole(authUser, email, wide, wide.crm_role);
  let it = sideFromWideRole(authUser, email, wide, wide.it_role);
  let dem = sideFromWideRole(authUser, email, wide, wide.dem_role);
  let forms = sideFromWideRole(authUser, email, wide, wide.forms_role);
  let accounts = sideFromWideRole(authUser, email, wide, wide.accounts_role);
  let report = sideFromWideRole(authUser, email, wide, wide.report_role);
  let purchasing = sideFromWideRole(authUser, email, wide, wide.purchasing_role);
  let design = sideFromWideRole(authUser, email, wide, wide.design_role);

  // Only IT / Management department elevates all portals — not CRM/IT role tags.
  if (elevatedDepartment) {
    crm = withElevatedSide(authUser, email, crm);
    it = withElevatedSide(authUser, email, it);
    dem = withElevatedSide(authUser, email, dem);
    forms = withElevatedSide(authUser, email, forms);
    accounts = withElevatedSide(authUser, email, accounts);
    report = withElevatedSide(authUser, email, report);
    purchasing = withElevatedSide(authUser, email, purchasing);
    design = withElevatedSide(authUser, email, design);
  }

  return {
    email: normalizeEmail(email),
    departmentSlug: departmentSlug ? departmentSlug.trim().toLowerCase() : null,
    elevatedDepartment,
    crm,
    it,
    dem,
    forms,
    accounts,
    report,
    purchasing,
    design,
  };
}

function syntheticElevatedProfile(authUser: User, email: string): CrmUser {
  return {
    id: authUser.id,
    username: emailToUsername(email),
    display_name: formatDisplayPersonName(emailToUsername(email)),
    role: 'it',
    email: normalizeEmail(email),
  };
}

function withElevatedSide(
  authUser: User,
  email: string,
  side: PortalSideAccess,
): PortalSideAccess {
  if (side.hasAccess && side.profile) {
    return {
      hasAccess: true,
      profile: { ...side.profile, role: 'it' },
    };
  }
  return { hasAccess: true, profile: syntheticElevatedProfile(authUser, email) };
}

export async function resolvePortalAccess(authUser: User): Promise<PortalAccessMap> {
  const email = resolveAuthEmail(authUser);
  if (!email) {
    return {
      email: null,
      departmentSlug: null,
      elevatedDepartment: null,
      crm: { hasAccess: false, profile: null },
      it: { hasAccess: false, profile: null },
      dem: { hasAccess: false, profile: null },
      forms: { hasAccess: false, profile: null },
      accounts: { hasAccess: false, profile: null },
      report: { hasAccess: false, profile: null },
      purchasing: { hasAccess: false, profile: null },
      design: { hasAccess: false, profile: null },
    };
  }

  const [wideRow, departmentSlug] = await Promise.all([
    fetchOwnPortalAccessRow(email, authUser.id).catch(() => null),
    fetchOwnPortalDepartmentSlug(email, authUser.id).catch(() => null),
  ]);

  // Membership is on portal_access only (CRM/IT allowlists are views after 261).
  if (wideRow && !wideRow.removed_at) {
    return mapFromWideRow(authUser, email, wideRow, departmentSlug);
  }

  const elevatedDepartment: ElevatedDepartmentSlug | null = isElevatedDepartmentSlug(departmentSlug)
    ? departmentSlug
    : null;

  const empty: PortalSideAccess = { hasAccess: false, profile: null };
  let crm = empty;
  let it = empty;
  let dem = empty;
  let forms = empty;
  let accounts = empty;
  let report = empty;
  let purchasing = empty;
  let design = empty;

  if (elevatedDepartment) {
    crm = withElevatedSide(authUser, email, crm);
    it = withElevatedSide(authUser, email, it);
    dem = withElevatedSide(authUser, email, dem);
    forms = withElevatedSide(authUser, email, forms);
    accounts = withElevatedSide(authUser, email, accounts);
    report = withElevatedSide(authUser, email, report);
    purchasing = withElevatedSide(authUser, email, purchasing);
    design = withElevatedSide(authUser, email, design);
  }

  return {
    email: normalizeEmail(email),
    departmentSlug: departmentSlug ? departmentSlug.trim().toLowerCase() : null,
    elevatedDepartment,
    crm,
    it,
    dem,
    forms,
    accounts,
    report,
    purchasing,
    design,
  };
}

export function portalSideAccess(map: PortalAccessMap, kind: PortalKind): PortalSideAccess {
  if (kind === 'it') return map.it;
  if (kind === 'dem') return map.dem;
  if (kind === 'forms') return map.forms;
  if (kind === 'inventory') return map.report;
  if (kind === 'accounts') return map.accounts;
  if (kind === 'report') return map.report;
  if (kind === 'purchasing') return map.purchasing;
  if (kind === 'design') return map.design;
  return map.crm;
}

export function activePortalKindFromLocation(pathname = window.location.pathname): PortalKind | null {
  return portalKindFromPath(pathname);
}

export function currentPortalAccess(
  map: PortalAccessMap,
  kind: PortalKind | null = activePortalKindFromLocation(),
): PortalSideAccess {
  if (!kind) return { hasAccess: false, profile: null };
  return portalSideAccess(map, kind);
}

/**
 * Portal picker: IT / management, or anyone with access to both workspaces.
 * Other roles with a single workspace go straight in (roles are per allowlist).
 */
export function shouldShowPortalChooser(
  map: PortalAccessMap,
  pathname = window.location.pathname,
): boolean {
  const count = [
    map.crm,
    map.it,
    map.dem,
    map.forms,
    map.report,
    map.accounts,
    map.purchasing,
    map.design,
  ].filter((s) => s.hasAccess).length;
  if (count === 0) return false;
  if (!isNeutralAuthPath(pathname)) return false;
  if (crossPortalUser(map)) return true;
  if (count > 1) return true;
  return false;
}

/** Default workspace when user does not need the portal picker. */
export function resolveDefaultPortalKind(
  map: PortalAccessMap,
  pathname = window.location.pathname,
): PortalKind | null {
  const pathKind = portalKindFromPath(pathname);
  if (pathKind && portalSideAccess(map, pathKind).hasAccess) return pathKind;

  const sole = solePortalAccess(map);
  if (sole) return sole;

  const choice = readPortalChoice();
  if (choice && portalSideAccess(map, choice).hasAccess) return choice;

  return null;
}

export function portalRoleLabel(role: UserRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function pickDisplayProfile(map: PortalAccessMap): CrmUser | null {
  const pathKind = activePortalKindFromLocation();
  if (pathKind) {
    const side = portalSideAccess(map, pathKind);
    if (side.profile) return side.profile;
  }
  const defaultKind = resolveDefaultPortalKind(map);
  if (defaultKind) {
    const side = portalSideAccess(map, defaultKind);
    if (side.profile) return side.profile;
  }
  if (map.crm.profile) return map.crm.profile;
  if (map.it.profile) return map.it.profile;
  if (map.report.profile) return map.report.profile;
  if (map.accounts.profile) return map.accounts.profile;
  if (map.purchasing.profile) return map.purchasing.profile;
  if (map.design.profile) return map.design.profile;
  if (map.dem.profile) return map.dem.profile;
  if (map.forms.profile) return map.forms.profile;
  return null;
}
